// Chakra imports
import {
  Box,
  Flex,
  Grid,
  Text,
  useColorModeValue,
  Button,
  HStack,
  VStack,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Badge,
  Image,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Select,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Spinner,
} from "@chakra-ui/react";
// Custom components
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import React, { useState } from "react";
import { FaPlus, FaFileCsv, FaDownload, FaStar, FaRegStar } from "react-icons/fa";
import logo from "assets/img/avatars/placeholder.png";
import { customerService } from "services/customerService";
import { invoiceService } from "services/invoiceService";
import { DownloadIcon } from "@chakra-ui/icons";
import { useToast } from "@chakra-ui/react";
import { FaEllipsisV } from "react-icons/fa";

// Star Rating Display Component
const StarRating = ({ rating, count, size = "14px", showText = true }) => {
  const starColor = useColorModeValue("#FFD700", "#FFD700");
  const fullStars = Math.floor(rating || 0);
  const hasHalfStar = (rating || 0) - fullStars >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  return (
    <HStack spacing="4px" align="center">
      {[...Array(fullStars)].map((_, i) => (
        <FaStar key={`full-${i}`} color={starColor} size={size} />
      ))}
      {hasHalfStar && (
        <Box position="relative" display="inline-block">
          <FaRegStar color={starColor} size={size} />
          <Box
            position="absolute"
            left="0"
            top="0"
            width="50%"
            overflow="hidden"
            style={{ pointerEvents: 'none' }}
          >
            <FaStar color={starColor} size={size} />
          </Box>
        </Box>
      )}
      {[...Array(emptyStars)].map((_, i) => (
        <FaRegStar key={`empty-${i}`} color={starColor} size={size} />
      ))}
      {showText && (
        <Text fontSize="sm" color="gray.500" ml="4px">
          {rating ? rating.toFixed(1) : '0.0'} {count ? `(${count})` : ''}
        </Text>
      )}
    </HStack>
  );
};

// Interactive Star Rating Component (for rating modal)
const InteractiveStarRating = ({ value, onChange, size = "24px" }) => {
  const starColor = useColorModeValue("#FFD700", "#FFD700");
  const hoverColor = useColorModeValue("#FFA500", "#FFA500");
  const [hoveredStar, setHoveredStar] = React.useState(null);

  return (
    <HStack spacing="8px">
      {[1, 2, 3, 4, 5].map((star) => {
        const isFilled = star <= (hoveredStar || value);
        const currentColor = hoveredStar && star <= hoveredStar ? hoverColor : starColor;
        
        return (
          <Box
            key={star}
            as="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => setHoveredStar(star)}
            onMouseLeave={() => setHoveredStar(null)}
            cursor="pointer"
            transition="transform 0.2s"
            _hover={{ transform: "scale(1.2)" }}
            type="button"
          >
            {isFilled ? (
              <FaStar color={currentColor} size={size} />
            ) : (
              <FaRegStar color={currentColor} size={size} />
            )}
          </Box>
        );
      })}
      <Text fontSize="md" color="gray.600" ml="8px">
        {hoveredStar || value} / 5
      </Text>
    </HStack>
  );
};

// Customer Table Row Component
const CustomerTableRow = ({ customer, onEdit, onDelete, onViewProfile, onRecordPayment, onCreateSale, onRate }) => {
  const textColor = useColorModeValue("gray.700", "white");

  const getStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "orange";
      case "Paid":
        return "blue";
      default:
        return "gray";
    }
  };

  return (
    <Tr>
      <Td>
        <Text fontSize="md" color={textColor} fontWeight="bold">{customer.serial_id || '-'}</Text>
      </Td>
      <Td minWidth={{ sm: "250px" }} pl="0px">
        <Flex align="center" py=".8rem" minWidth="100%" flexWrap="nowrap">
          <Image src={customer.avatar} w="30px" h="30px" me="18px" objectFit="cover" />
          <Flex direction="column">
            <Text
              fontSize="md"
              color={textColor}
              fontWeight="bold"
              minWidth="100%"
            >
              {customer.name}
            </Text>
            <Text fontSize="sm" color="gray.400" fontWeight="medium">
              {customer.email}
            </Text>
          </Flex>
        </Flex>
      </Td>

      <Td>
        <Text fontSize="md" color={textColor} fontWeight="bold">{customer.phone || '-'}</Text>
      </Td>

      <Td>
        <Text fontSize="md" color={textColor} fontWeight="bold">{customer.due}</Text>
      </Td>

      <Td>
        <Text fontSize="md" color={textColor} fontWeight="bold">{customer.advance}</Text>
      </Td>

      <Td>
        {customer.ratingAverage !== undefined && customer.ratingAverage !== null ? (
          <StarRating rating={customer.ratingAverage} count={customer.ratingCount} />
        ) : (
          <Text fontSize="sm" color="gray.400">No rating</Text>
        )}
      </Td>

      <Td isNumeric>
        <Menu placement="bottom-end">
          <MenuButton as={IconButton} icon={<FaEllipsisV />} variant="ghost" size="sm" aria-label="Actions" />
          <MenuList>
            <MenuItem onClick={() => onViewProfile(customer)}>View Profile</MenuItem>
            <MenuItem onClick={() => onEdit(customer)}>Edit</MenuItem>
            <MenuItem onClick={() => onRecordPayment(customer)}>Record Payment/Advance</MenuItem>
            <MenuItem onClick={() => onCreateSale(customer)}>Create Sale</MenuItem>
            <MenuItem onClick={() => onRate(customer)}>Rate</MenuItem>
            <MenuItem color="red.500" onClick={() => onDelete(customer)}>Delete</MenuItem>
          </MenuList>
        </Menu>
      </Td>
    </Tr>
  );
};

function CustomerManagement() {
  const textColor = useColorModeValue("gray.700", "white");
  const cardBg = useColorModeValue("white", "gray.700");
  const cardShadow = useColorModeValue("0 4px 20px rgba(0,0,0,0.06)", "0 4px 20px rgba(0,0,0,0.3)");
  const toast = useToast();
  const nowDateTimeLocal = () => new Date().toISOString().slice(0, 16);
  const getDefaultPaymentForm = () => ({
    type: 'payment',
    amount: '',
    description: '',
    advance_start_date: '',
    advance_end_date: '',
    occurred_at: nowDateTimeLocal(),
    payment_method: 'cash',
    deposit_account_id: ''
  });
  
  const { isOpen: isAddOpen, onOpen: onAddOpen, onClose: onAddClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isProfileOpen, onOpen: onProfileOpen, onClose: onProfileClose } = useDisclosure();
  const { isOpen: isPaymentOpen, onOpen: onPaymentOpen, onClose: onPaymentClose } = useDisclosure();
  const { isOpen: isSaleOpen, onOpen: onSaleOpen, onClose: onSaleClose } = useDisclosure();
  const { isOpen: isRateOpen, onOpen: onRateOpen, onClose: onRateClose } = useDisclosure();
  
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [downloadingIds, setDownloadingIds] = useState(new Set());

  const [editingCustomer, setEditingCustomer] = useState(null);
  const [newCustomer, setNewCustomer] = useState({
    name: "",
    phone: "",
    address: "",
    serial_id: ""
  });
  const [profileData, setProfileData] = useState(null);
  const [paymentForm, setPaymentForm] = useState(() => getDefaultPaymentForm());
  const [savingPayment, setSavingPayment] = useState(false);
  const [saleForm, setSaleForm] = useState({ items: [], paid_amount: '', reference: '', sale_date: '' });
  const [ratingForm, setRatingForm] = useState({ stars: 5, note: '' });

  const formatPaymentSummary = React.useCallback((record) => {
    if (!record) return '-';
    const breakdownArray = Array.isArray(record.payment_breakdown)
      ? record.payment_breakdown
      : (Array.isArray(record.payments) ? record.payments : []);
    const formatLabel = (label) => {
      if (!label) return '-';
      const cleaned = String(label).replace(/_/g, ' ').trim();
      const lower = cleaned.toLowerCase();
      if (lower === 'bank') return 'Online';
      if (lower === 'cash') return 'Cash';
      return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
    };
    if (breakdownArray.length > 0) {
      return breakdownArray
        .filter(item => item && (item.payment_method || item.method))
        .map(item => {
          const name = formatLabel(item.payment_method || item.method);
          const amount = Number(item.amount || item.paid_amount || 0);
          return amount > 0 ? `${name} PKR ${amount.toFixed(2)}` : name;
        })
        .join(' + ');
    }
    if (record.payment_method === 'split') return 'Cash + Online';
    return formatLabel(record.payment_method) || '-';
  }, []);

  const loadCustomers = async () => {
    try { setLoading(true); const resp = await customerService.list({}); const list = resp?.data?.data || resp?.data || resp || []; setCustomers(list); }
    catch (e) { /* silent */ } finally { setLoading(false); }
  };

  React.useEffect(() => { loadCustomers(); }, []);

  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    onEditOpen();
  };

  const handleUpdateCustomer = async () => {
    if (!editingCustomer) return;
    try {
      const phoneSanitized = (editingCustomer.phone || '').toString().replace(/[^0-9]/g, '');
      await customerService.update(editingCustomer.id, { name: editingCustomer.name, phone: phoneSanitized, address: editingCustomer.address || '', serial_id: editingCustomer.serial_id || '' });
      onEditClose();
      setEditingCustomer(null);
      await loadCustomers();
    } catch (e) { alert(e?.message || 'Failed to update'); }
  };

  const parseDateTimeLocalToISO = (value) => {
    if (!value) return undefined;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return undefined;
    return parsed.toISOString();
  };

  const handlePaymentModalClose = () => {
    setPaymentForm(getDefaultPaymentForm());
    onPaymentClose();
  };

  const handlePaymentSubmit = async () => {
    if (!selectedCustomer) {
      toast({
        title: 'Select a customer first',
        status: 'warning',
        duration: 3000,
        isClosable: true
      });
      return;
    }
    const amountValue = Number(paymentForm.amount || 0);
    if (!Number.isFinite(amountValue) || amountValue <= 0) {
      toast({
        title: 'Enter a valid amount',
        description: 'Amount must be greater than zero.',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    const description = paymentForm.description?.trim();
    setSavingPayment(true);
    try {
      if (paymentForm.type === 'add_due') {
        const payload = {
          amount: amountValue,
          ...(description ? { description } : {})
        };
        const occurredAt = parseDateTimeLocalToISO(paymentForm.occurred_at);
        if (occurredAt) payload.occurred_at = occurredAt;
        if (paymentForm.payment_method) payload.payment_method = paymentForm.payment_method;
        await customerService.addDue(selectedCustomer.id, payload);
        toast({
          title: 'Due balance updated',
          description: 'Manual due adjustment invoice created.',
          status: 'success',
          duration: 3000,
          isClosable: true
        });
      } else if (paymentForm.type === 'add_advance') {
        const payload = {
          amount: amountValue,
          ...(description ? { description } : {})
        };
        if (paymentForm.advance_start_date) payload.advance_start_date = paymentForm.advance_start_date;
        if (paymentForm.advance_end_date) payload.advance_end_date = paymentForm.advance_end_date;
        const occurredAt = parseDateTimeLocalToISO(paymentForm.occurred_at);
        if (occurredAt) payload.occurred_at = occurredAt;
        if (paymentForm.payment_method) payload.payment_method = paymentForm.payment_method;
        if (paymentForm.deposit_account_id) payload.deposit_account_id = paymentForm.deposit_account_id;
        await customerService.addAdvance(selectedCustomer.id, payload);
        toast({
          title: 'Advance balance updated',
          description: 'Manual advance invoice created.',
          status: 'success',
          duration: 3000,
          isClosable: true
        });
      } else {
        const payload = {
          type: paymentForm.type,
          amount: amountValue,
          ...(description ? { description } : {})
        };
        if (paymentForm.advance_start_date) payload.advance_start_date = paymentForm.advance_start_date;
        if (paymentForm.advance_end_date) payload.advance_end_date = paymentForm.advance_end_date;
        await customerService.recordPayment(selectedCustomer.id, payload);
        toast({
          title: paymentForm.type === 'advance' ? 'Advance recorded' : 'Payment recorded',
          status: 'success',
          duration: 3000,
          isClosable: true
        });
      }
      handlePaymentModalClose();
      await loadCustomers();
    } catch (e) {
      toast({
        title: 'Action failed',
        description: e?.message || 'Unable to complete request',
        status: 'error',
        duration: 4000,
        isClosable: true
      });
    } finally {
      setSavingPayment(false);
    }
  };
  const manualDueSelected = paymentForm.type === 'add_due';
  const manualAdvanceSelected = paymentForm.type === 'add_advance';
  const requiresAdvanceWindow = paymentForm.type === 'advance' || manualAdvanceSelected;
  const showManualTimingFields = manualDueSelected || manualAdvanceSelected;
  const showManualPaymentFields = manualDueSelected || manualAdvanceSelected;

  const handleAddCustomer = async () => {
    if (!newCustomer.name) return;
    try {
      const phoneSanitized = (newCustomer.phone || '').toString().replace(/[^0-9]/g, '');
      await customerService.create({ name: newCustomer.name, phone: phoneSanitized, address: newCustomer.address || '', serial_id: newCustomer.serial_id || '' });
      onAddClose();
      setNewCustomer({ name: "", phone: '', address: '', serial_id: '' });
      await loadCustomers();
    } catch (e) { alert(e?.message || 'Failed to create customer'); }
  };

  const handleImportCSV = () => {
    // CSV import functionality would go here
    alert("CSV import functionality would be implemented here");
  };

  const handleExportCSV = () => {
    // CSV export functionality would go here
    alert("CSV export functionality would be implemented here");
  };

  const handleDownloadInvoice = async (invoiceId) => {
    if (downloadingIds.has(invoiceId)) return;
    
    try {
      setDownloadingIds(prev => new Set(prev).add(invoiceId));
      await invoiceService.downloadInvoice(invoiceId);
      toast({
        title: 'Download successful',
        description: 'Invoice downloaded successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Download failed',
        description: error.message || 'Failed to download invoice',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setDownloadingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(invoiceId);
        return newSet;
      });
    }
  };

  return (
    <Flex direction='column' pt={{ base: "120px", md: "75px" }}>
      {/* Header Section */}
      <Box mb='24px'>
        <Flex direction='column' w='100%'>
          <Text fontSize='2xl' color={textColor} fontWeight='bold' mb='8px'>
            Customer Management
          </Text>
          
          {/* Action Buttons */}
          <Flex
            direction={{ sm: "column", lg: "row" }}
            justify='space-between'
            align={{ sm: "start", lg: "center" }}
            w='100%'
            gap='16px'>
            
            {/* Left Side - Add New Customer */}
            <Button
              leftIcon={<FaPlus />}
              colorScheme='teal'
              bg='#FF8D28'
              color='white'
              _hover={{ bg: '#E67E22' }}
              onClick={onAddOpen}
              size='md'>
              Add New Customer
            </Button>

            {/* Right Side - Import/Export */}
            <HStack spacing='16px'>
              <Button
                leftIcon={<FaFileCsv />}
                colorScheme='teal'
                bg='#FF8D28'
                color='white'
                _hover={{ bg: '#E67E22' }}
                size='md'>
                Import CSV
              </Button>
              <Button
                leftIcon={<FaDownload />}
                variant='outline'
                colorScheme='teal'
                borderColor='gray.300'
                color='gray.600'
                _hover={{ bg: 'gray.50' }}
                size='md'>
                Export as CSV
              </Button>
            </HStack>
          </Flex>
        </Flex>
      </Box>

      {/* Customer Profiles Table */}
      <Card bg={cardBg} boxShadow={cardShadow}>

        <CardBody>
          <Table variant='simple' color={textColor}>
            <Thead>
              <Tr>
                <Th color='gray.400' fontSize='sm' fontWeight='semibold'>Serial ID</Th>
                <Th color='gray.400' fontSize='sm' fontWeight='semibold'>Customer</Th>
                <Th color='gray.400' fontSize='sm' fontWeight='semibold'>Phone</Th>
                <Th color='gray.400' fontSize='sm' fontWeight='semibold'>Due Balance</Th>
                <Th color='gray.400' fontSize='sm' fontWeight='semibold'>Advance Balance</Th>
                <Th color='gray.400' fontSize='sm' fontWeight='semibold'>Rating</Th>
                <Th color='gray.400' fontSize='sm' fontWeight='semibold'>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {loading && (
                <Tr>
                  <Td colSpan={7} py='48px' textAlign='center'>
                    <Spinner thickness='3px' speed='0.65s' emptyColor='gray.200' color='#FF8D28' size='lg' />
                  </Td>
                </Tr>
              )}
              {!loading && customers.length === 0 && (
                <Tr>
                  <Td colSpan={7} py='48px'>
                    <Box textAlign='center' color='gray.500'>
                      <Text fontWeight='bold' mb='2'>No customers found</Text>
                      <Text fontSize='sm'>Use the Add New Customer button to create one.</Text>
                    </Box>
                  </Td>
                </Tr>
              )}
              {!loading && customers.map((c) => (
                <CustomerTableRow 
                  key={c.id} 
                  customer={{
                    id: c.id,
                    name: c.name,
                    phone: c.phone || '',
                    serial_id: c.serial_id || '',
                    avatar: logo,
                    due: `PKR.${Number(c.due_balance || 0).toFixed(2)}`,
                    advance: `PKR.${Number(c.advance_balance || 0).toFixed(2)}`,
                    ratingAverage: c.rating_average ? Number(c.rating_average) : null,
                    ratingCount: c.rating_count || 0
                  }}
                  onEdit={handleEditCustomer}
                  onDelete={async (row) => { if (!window.confirm('Delete customer?')) return; try { await customerService.delete(row.id); await loadCustomers(); } catch (e) { alert(e?.message || 'Delete failed'); } }}
                  onViewProfile={(row) => { window.location.href = `#/admin/customers/${row.id}`; }}
                  onRecordPayment={(row) => { setSelectedCustomer(row); setPaymentForm(getDefaultPaymentForm()); onPaymentOpen(); }}
                  onCreateSale={(row) => { setSelectedCustomer(row); onSaleOpen(); }}
                  onRate={(row) => { setSelectedCustomer(row); setRatingForm({ stars: 5, note: '' }); onRateOpen(); }}
                />
              ))}
            </Tbody>
          </Table>
        </CardBody>
      </Card>

      {/* Profile Modal */}
      <Modal isOpen={isProfileOpen} onClose={onProfileClose} size='xl'>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color={textColor}>Customer Profile</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {!profileData ? (
              <Flex align='center' justify='center' py='24px'><Spinner /></Flex>
            ) : (
              <VStack align='stretch' spacing='12px'>
                <Text fontWeight='bold'>{profileData.name}</Text>
                <Text color='gray.500'>Due: PKR {Number(profileData.due_balance || 0).toFixed(2)} • Advance: PKR {Number(profileData.advance_balance || 0).toFixed(2)}</Text>
                <Text fontWeight='semibold'>Recent Sales</Text>
                <Box maxH='160px' overflowY='auto'>
                  {(profileData.sales || []).slice(0,5).map((s)=> (
                    <Text key={s.id} color='gray.600'>#{s.id} • PKR {Number(s.total_amount||0).toFixed(2)} • Paid {Number(s.paid_amount||0).toFixed(2)} • Due {Number(s.due_amount||0).toFixed(2)}</Text>
                  ))}
                </Box>
                <Text fontWeight='semibold' mt='2'>Recent Invoices</Text>
                <Box maxH='160px' overflowY='auto'>
                  {(profileData.invoices || []).slice(0,5).map((inv)=> (
                  <HStack key={inv.id} justify='space-between' align='flex-start' spacing='8px'>
                    <VStack align='flex-start' spacing='1'>
                      <Text color='gray.600'>
                        {inv.invoice_number || `#${inv.id}`} • PKR {Number(inv.total||0).toFixed(2)}
                      </Text>
                      <Text fontSize='xs' color='gray.500'>
                        Paid via {formatPaymentSummary(inv)}
                      </Text>
                    </VStack>
                    <IconButton
                      icon={<DownloadIcon />}
                      size='xs'
                      variant='ghost'
                      onClick={() => handleDownloadInvoice(inv.id)}
                      isLoading={downloadingIds.has(inv.id)}
                      aria-label='Download invoice'
                    />
                  </HStack>
                  ))}
                  {(!profileData.invoices || profileData.invoices.length===0) && (
                    <Text color='gray.500'>No invoices</Text>
                  )}
                </Box>
                <Text fontWeight='semibold'>Recent Transactions</Text>
                <Box maxH='160px' overflowY='auto'>
                  {(profileData.transactions || []).slice(0,5).map((t,i)=> (
                    <Text key={i} color='gray.600'>{t.type} • {t.direction} • PKR {Number(t.amount||0).toFixed(2)}</Text>
                  ))}
                </Box>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Payment Modal */}
      <Modal isOpen={isPaymentOpen} onClose={handlePaymentModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color={textColor}>Record Payment / Manual Adjustment</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing='12px'>
              <FormControl>
                <FormLabel>Type</FormLabel>
                <Select value={paymentForm.type} onChange={(e)=> setPaymentForm({...paymentForm, type: e.target.value})}>
                  <option value='payment'>Record payment (reduces due)</option>
                  <option value='advance'>Record advance (uses advance balance)</option>
                  <option value='add_due'>Add due balance manually</option>
                  <option value='add_advance'>Add advance balance manually</option>
                </Select>
                <FormHelperText fontSize='xs'>
                  Manual adjustments automatically create the accounting invoices described in CUSTOMER_APIS.md.
                </FormHelperText>
              </FormControl>
              <FormControl>
                <FormLabel>Amount</FormLabel>
                <Input type='number' min='0' step='any' value={paymentForm.amount} onChange={(e)=> setPaymentForm({...paymentForm, amount: e.target.value})} />
              </FormControl>
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Input value={paymentForm.description} onChange={(e)=> setPaymentForm({...paymentForm, description: e.target.value})} />
              </FormControl>
              {requiresAdvanceWindow && (
                <HStack spacing='12px' align='flex-start' w='100%'>
                  <FormControl>
                    <FormLabel>Advance Start Date</FormLabel>
                    <Input type='date' value={paymentForm.advance_start_date} onChange={(e)=> setPaymentForm({...paymentForm, advance_start_date: e.target.value})} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Advance End Date</FormLabel>
                    <Input type='date' value={paymentForm.advance_end_date} onChange={(e)=> setPaymentForm({...paymentForm, advance_end_date: e.target.value})} />
                  </FormControl>
                </HStack>
              )}
              {showManualTimingFields && (
                <FormControl>
                  <FormLabel>Occurred At</FormLabel>
                  <Input type='datetime-local' value={paymentForm.occurred_at} onChange={(e)=> setPaymentForm({...paymentForm, occurred_at: e.target.value})} />
                </FormControl>
              )}
              {showManualPaymentFields && (
                <FormControl>
                  <FormLabel>Payment Method</FormLabel>
                  <Select value={paymentForm.payment_method} onChange={(e)=> setPaymentForm({...paymentForm, payment_method: e.target.value})}>
                    <option value='cash'>Cash</option>
                    <option value='card'>Card</option>
                    <option value='other'>Other</option>
                  </Select>
                </FormControl>
              )}
              {manualAdvanceSelected && (
                <FormControl>
                  <FormLabel>Deposit Account ID</FormLabel>
                  <Input value={paymentForm.deposit_account_id} onChange={(e)=> setPaymentForm({...paymentForm, deposit_account_id: e.target.value})} placeholder='Optional ledger account id' />
                  <FormHelperText fontSize='xs'>Optional: specify the cash/bank ledger receiving this advance.</FormHelperText>
                </FormControl>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={handlePaymentModalClose} mr='3'>Cancel</Button>
            <Button bg='#FF8D28' color='white' _hover={{bg:'#E67E22'}} onClick={handlePaymentSubmit} isLoading={savingPayment}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Rate Modal */}
      <Modal isOpen={isRateOpen} onClose={onRateClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color={textColor}>
            Rate Customer {selectedCustomer?.name ? `- ${selectedCustomer.name}` : ''}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing='20px' align='stretch'>
              <FormControl>
                <FormLabel fontSize='md' fontWeight='semibold' mb='12px'>Rating</FormLabel>
                <InteractiveStarRating 
                  value={ratingForm.stars} 
                  onChange={(stars) => setRatingForm({...ratingForm, stars})} 
                />
              </FormControl>
              <FormControl>
                <FormLabel>Note (Optional)</FormLabel>
                <Input 
                  placeholder='Add a note about this customer...' 
                  value={ratingForm.note} 
                  onChange={(e)=> setRatingForm({...ratingForm, note: e.target.value})} 
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onRateClose} mr='3'>Cancel</Button>
            <Button 
              bg='#FF8D28' 
              color='white' 
              _hover={{bg:'#E67E22'}} 
              onClick={async ()=> { 
                try { 
                  await customerService.rate(selectedCustomer.id, ratingForm); 
                  toast({
                    title: 'Rating saved',
                    description: `Customer rated ${ratingForm.stars} stars`,
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                  });
                  onRateClose(); 
                  setRatingForm({ stars: 5, note: '' });
                  await loadCustomers(); 
                } catch(e){ 
                  toast({
                    title: 'Rating failed',
                    description: e?.message || 'Failed to save rating',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                  });
                } 
              }}
            >
              Save Rating
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Sale Modal (minimal: one item) */}
      <Modal isOpen={isSaleOpen} onClose={onSaleClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color={textColor}>Create Sale</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing='12px'>
              <FormControl>
                <FormLabel>Stock Item ID</FormLabel>
                <Input value={saleForm.stock_item_id || ''} onChange={(e)=> setSaleForm({...saleForm, stock_item_id: e.target.value})} />
              </FormControl>
              <FormControl>
                <FormLabel>Quantity</FormLabel>
                <Input type='number' step='any' value={saleForm.quantity || ''} onChange={(e)=> setSaleForm({...saleForm, quantity: e.target.value})} />
              </FormControl>
              <FormControl>
                <FormLabel>Unit Price</FormLabel>
                <Input type='number' step='any' value={saleForm.unit_price || ''} onChange={(e)=> setSaleForm({...saleForm, unit_price: e.target.value})} />
              </FormControl>
              <FormControl>
                <FormLabel>Paid Amount</FormLabel>
                <Input type='number' step='any' value={saleForm.paid_amount || ''} onChange={(e)=> setSaleForm({...saleForm, paid_amount: e.target.value})} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onSaleClose} mr='3'>Cancel</Button>
            <Button bg='#FF8D28' color='white' _hover={{bg:'#E67E22'}} onClick={async ()=> { try { const payload = { items: [{ stock_item_id: Number(saleForm.stock_item_id), quantity: Number(saleForm.quantity), unit_price: Number(saleForm.unit_price)}], paid_amount: Number(saleForm.paid_amount||0) }; await customerService.createSale(selectedCustomer.id, payload); onSaleClose(); await loadCustomers(); window.dispatchEvent(new CustomEvent('sale-created')); window.dispatchEvent(new CustomEvent('stock-updated')); } catch(e){ alert(e?.message||'Failed'); } }}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* Add New Customer Modal */}
      <Modal isOpen={isAddOpen} onClose={onAddClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color={textColor}>Add New Customer</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing="16px" align="stretch">
              <FormControl>
                <FormLabel fontSize="sm" color="gray.500">
                  Customer Name
                </FormLabel>
                <Input
                  value={newCustomer.name}
                  onChange={(e) => {
                    const { value } = e.target;
                    setNewCustomer(prev => ({
                      ...prev,
                      name: value
                    }));
                  }}
                  placeholder="Enter customer name"
                  size="md"
                />
              </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.500">Phone</FormLabel>
                  <Input
                    value={newCustomer.phone}
                    onChange={(e)=> { const val = e.target.value; setNewCustomer(prev=> ({...prev, phone: val})); }}
                    placeholder="e.g., +92..."
                    size="md"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.500">Address</FormLabel>
                  <Input
                    value={newCustomer.address}
                    onChange={(e)=> { const val = e.target.value; setNewCustomer(prev=> ({...prev, address: val})); }}
                    placeholder="Address"
                    size="md"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.500">Serial ID</FormLabel>
                  <Input
                    value={newCustomer.serial_id}
                    onChange={(e)=> { const val = e.target.value; setNewCustomer(prev=> ({...prev, serial_id: val})); }}
                    placeholder="e.g., CUST-001"
                    size="md"
                  />
                </FormControl>
              
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing="12px">
              <Button variant="outline" onClick={onAddClose}>
                Cancel
              </Button>
              <Button
                colorScheme="teal"
                bg="#FF8D28"
                color="white"
                _hover={{ bg: "#E67E22" }}
                onClick={handleAddCustomer}
                isDisabled={!newCustomer.name}
              >
                Add Customer
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Customer Modal */}
      <Modal isOpen={isEditOpen} onClose={onEditClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color={textColor}>Edit Customer</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {editingCustomer && (
              <VStack spacing="16px" align="stretch">
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.500">
                    Customer Name
                  </FormLabel>
                  <Input
                    value={editingCustomer.name}
                    onChange={(e) => setEditingCustomer(prev => ({
                      ...prev,
                      name: e.target.value
                    }))}
                    size="md"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.500">Phone</FormLabel>
                  <Input
                    value={editingCustomer.phone || ''}
                    onChange={(e)=> { const val = e.target.value; setEditingCustomer(prev=> (prev ? {...prev, phone: val} : { phone: val })); }}
                    size="md"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.500">Address</FormLabel>
                  <Input
                    value={editingCustomer.address || ''}
                    onChange={(e)=> { const val = e.target.value; setEditingCustomer(prev=> (prev ? {...prev, address: val} : { address: val })); }}
                    size="md"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.500">Serial ID</FormLabel>
                  <Input
                    value={editingCustomer.serial_id || ''}
                    onChange={(e)=> { const val = e.target.value; setEditingCustomer(prev=> (prev ? {...prev, serial_id: val} : { serial_id: val })); }}
                    placeholder="e.g., CUST-001"
                    size="md"
                  />
                </FormControl>
                
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <HStack spacing="12px">
              <Button variant="outline" onClick={onEditClose}>
                Cancel
              </Button>
              <Button
                colorScheme="teal"
                bg="#FF8D28"
                color="white"
                _hover={{ bg: "#E67E22" }}
                onClick={handleUpdateCustomer}
              >
                Update Customer
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}

export default CustomerManagement;
